import { Typography } from '@atlas-metrics/design-system';
import {
  WidgetDataType,
  WidgetNoneType,
} from '@atlas-metrics/microsite-types/dist/types/typeIndex';
import React, { ReactElement, useEffect, useState } from 'react';
import {
  EventCallbackInterface,
  StringOrNumberOrList,
  VictoryContainer,
} from 'victory';
import { VictoryPie } from 'victory-pie';

import { FormatValueFn } from '@/hooks/useFormatValue';

import { primaryColors, secondaryColors } from '@/styles/colorSchemes';

import ChartLegend from './ChartLegend';
import { randomizeChartColorsStartIdx } from './utils';
import Badge from '../ui/Badge';
import VerticalDivider from '../ui/VerticalDivider';

import { PieChartDisclosureResult } from '@/types';

type Props = {
  title: string;
  unit: string;
  dataType: WidgetDataType | WidgetNoneType;
  data: PieChartDisclosureResult[];
  formatValue: FormatValueFn;
};

type ExternalEventMutations = EventCallbackInterface<
  string | string[],
  StringOrNumberOrList
>[];

const innerRadiusValue = 165;
const expandingInnerRadiusValue = 190;

const PieChartWithLegends: React.FC<Props> = ({
  title,
  unit,
  data,
  dataType,
  formatValue,
}) => {
  const [externalMutations, setExternalMutations] = useState<
    ExternalEventMutations | undefined
  >(undefined);
  const [colors, setColors] = useState<string[]>();
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [isTouchDevice, setIsTouchDevice] = useState<boolean>(false);
  const sumData = data.reduce((sum, dataset) => sum + dataset.value, 0);

  useEffect(() => {
    // colors set should be random for each chart
    const chartColorsStartIdx = randomizeChartColorsStartIdx(data);
    setColors([
      ...primaryColors.slice(chartColorsStartIdx),
      ...primaryColors.slice(0, chartColorsStartIdx),
      ...secondaryColors,
    ]);
  }, [data]);

  useEffect(() => {
    if (
      typeof window !== 'undefined' &&
      ('ontouchstart' in window || navigator.maxTouchPoints > 0)
    ) {
      setIsTouchDevice(true);
    }
  }, []);

  const removeMutation = () => {
    setExternalMutations(undefined);
  };

  const clearMutation = {
    childName: 'donut',
    target: ['data'],
    eventKey: 'all',
    mutation: () => ({ innerRadius: innerRadiusValue, style: undefined }),
    callback: removeMutation,
  };

  // trigger from legend row to pie chart
  const handleHover = (index: number) => {
    setActiveIndex(index);
    const mutations = [
      {
        childName: 'donut',
        target: ['data'],
        eventKey: 'all',
        mutation: () => ({
          innerRadius: innerRadiusValue,
          style: { fill: '#E5E4E4' },
        }),
      },
      {
        childName: 'donut',
        target: ['data'],
        eventKey: index.toString(),
        mutation: () => ({
          innerRadius: expandingInnerRadiusValue,
          style: undefined,
        }),
        callback: removeMutation,
      },
    ];

    setExternalMutations(mutations);
  };

  const clearHover = () => {
    setExternalMutations([clearMutation]);
    setActiveIndex(null);
  };

  const getChartLegend = () => {
    const [legends] = data.reduce<[ReactElement[], number]>(
      ([legends, idx], curr, listIndex, all) => {
        const hasValue = typeof curr.value === 'number' && curr.value > 0;
        const currentIdx = hasValue ? idx : all.length + listIndex;
        return [
          [
            ...legends,
            <ChartLegend
              title={curr.name}
              number={curr.value}
              dataType={dataType}
              key={currentIdx}
              color={colors && colors[currentIdx]}
              onMouseOver={() => handleHover(currentIdx)}
              onMouseOut={clearHover}
              formatValue={formatValue}
              className={
                activeIndex === currentIdx ? 'bg-ds-grey-50' : undefined
              }
            />,
          ],
          hasValue ? idx + 1 : idx,
        ];
      },
      [[], 0]
    );

    return legends;
  };

  return (
    <div className='webL:flex'>
      <div className='flex justify-center webL:self-center'>
        <div className='relative flex h-[256px] w-[256px] items-center justify-center'>
          <div className='flex flex-col items-center gap-4'>
            <Typography
              variant='body-l'
              className={
                activeIndex !== null ? 'text-ds-grey-950' : 'text-ds-grey-700'
              }
            >
              {title}
            </Typography>
            <Typography variant='h4' className='font-semibold text-ds-grey-950'>
              {typeof formatValue === 'function' &&
                formatValue(sumData, dataType)}
            </Typography>
            <Badge
              variant={activeIndex !== null ? 'brand' : 'default'}
              size='small'
            >
              {unit}
            </Badge>
          </div>
          <div className='absolute h-full w-full'>
            <VictoryPie
              name='donut'
              data={data.filter(
                (d) => typeof d?.value === 'number' && d.value > 0
              )}
              x='result'
              y='value'
              labelComponent={<></>}
              containerComponent={
                isTouchDevice ? (
                  <VictoryContainer
                    style={{
                      pointerEvents: 'auto',
                      userSelect: 'auto',
                      touchAction: 'auto',
                    }}
                  />
                ) : undefined
              }
              // trigger from pie chart to legend row
              events={[
                {
                  target: 'data',
                  eventHandlers: {
                    onMouseOver: () => {
                      return [
                        {
                          target: ['data'],
                          eventKey: 'all',
                          mutation: () => ({
                            style: { fill: '#E5E4E4', cursor: 'pointer' },
                          }),
                        },
                        {
                          target: ['data'],
                          mutation: (args) => {
                            setActiveIndex(args.index);
                            return {
                              innerRadius: expandingInnerRadiusValue,
                            };
                          },
                        },
                      ];
                    },
                    onMouseOut: () => {
                      return [
                        {
                          target: 'data',
                          eventKey: 'all',
                          mutation: () => null,
                          callback: () => setActiveIndex(null),
                        },
                      ];
                    },
                  },
                },
              ]}
              cornerRadius={4}
              padAngle={2}
              colorScale={colors}
              innerRadius={innerRadiusValue}
              animate={{
                duration: 500,
              }}
              externalEventMutations={externalMutations}
            />
          </div>
        </div>
      </div>

      <VerticalDivider className='border-0 border-l border-ds-grey-400/30' />
      <div className='m-4 grid content-center gap-y-2 webL:w-full'>
        {getChartLegend()}
        {data.length > 1 && (
          <ChartLegend
            title='Total'
            formatValue={formatValue}
            number={sumData}
            dataType={dataType}
            className='pointer-events-none touch-none'
            noIndentation
          />
        )}
      </div>
    </div>
  );
};

export default PieChartWithLegends;
